import { date } from '@beelday/common'
import { useSelector } from 'app-redux'
import { UserId } from 'common/types'
import { redux as scriptRedux } from 'features/script'
import { usePresenterConsole } from 'presenter-mode/presenter-console'
import { useMemo } from 'react'
import { roomEndUsers } from 'room/common/redux'
import { useStageSurface } from 'room/common/stage-surface'
import useIsTrainer from 'room/common/use-is-trainer'
import { uniq } from 'lodash'
import { InPersonLobbyMoment } from './in-person-lobby-moment'
import {
	chunkHours,
	LobbyRole,
	lobbyView,
	LobbyView,
	PresenterStatus,
} from './lobby-view'

export type LobbyScreen = {
	view: LobbyView
	role: LobbyRole
	/** The lead trainer, whose face goes with "Led by". */
	trainerId?: UserId
	/** The people in the room, who are the faces beside the count. */
	presentIds: UserId[]
}

/**
 * Who is looking: the trainer in their own window, or everyone else - the
 * projector included, which is the room's wall and is fed the trainer's
 * session (so `useIsTrainer` is true there) but has nothing to press.
 */
export const useLobbyRole = (): LobbyRole => {
	const isTrainer = useIsTrainer()
	const surface = useStageSurface()
	return isTrainer && surface !== 'projector' ? 'trainer' : 'participant'
}

const usePresenterStatus = (): PresenterStatus => {
	const presenter = usePresenterConsole()
	if (!presenter) {
		return 'unavailable'
	}
	return presenter.state.phase === 'idle' ? 'idle' : 'running'
}

/**
 * Everything the in-person lobby shows, read from the store and put through
 * `lobbyView`. `undefined` while the lobby moment is being read: until the
 * screen knows whether this is a break, it must not say either.
 */
export const useLobbyScreen = ({
	moment,
	at,
}: InPersonLobbyMoment): LobbyScreen | undefined => {
	const role = useLobbyRole()
	const presenter = usePresenterStatus()
	const dateFormat = date.useDateFormat()
	const training = useSelector(state => state.sessionInfo.training)
	const chunk = useSelector(
		state => state.script.chunks[state.script.currentSessionChunkIndex]
	)
	const agendaHasItems = useSelector(scriptRedux.selectScriptHasItems)
	// Sections only: a break is no place for the tool rows, which the agenda
	// keeps for the facilitator.
	const nextSection = useSelector(scriptRedux.selectNextScriptStep)?.item.title
	const endUsers = useSelector(roomEndUsers)

	const presentIds = useMemo(
		() => uniq(endUsers.map(user => user.id)),
		[endUsers]
	)
	const invited = training?.participants.length ?? 0
	const { formatTime } = dateFormat

	return useMemo(
		() =>
			moment
				? {
						role,
						trainerId: training?.upperEchelon.userId,
						presentIds,
						view: lobbyView({
							phase: moment,
							role,
							subject: training?.subject,
							hours: chunkHours(chunk, formatTime),
							trainerName: training?.upperEchelon.name,
							plannedTime: at ? formatTime(at) : undefined,
							nextSection,
							attendance: { present: presentIds.length, expected: invited },
							presenter,
							agendaHasItems,
						}),
				  }
				: undefined,
		[
			agendaHasItems,
			at,
			chunk,
			formatTime,
			invited,
			moment,
			nextSection,
			presenter,
			presentIds,
			role,
			training?.subject,
			training?.upperEchelon.name,
			training?.upperEchelon.userId,
		]
	)
}
